import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Scale, Landmark, FileText, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, RefreshCcw } from "lucide-react";

/**
 * AdminResearchQualityTab — visibility into how well the retrieval-grounded
 * legal AI is performing. Surfaces:
 *   • Confidence-signal histogram (source_grounded / general_info / unable_to_verify)
 *   • Top categories + states where indexed sources keep missing
 *   • Recent weak-answer questions for content-improvement triage
 *   • Provider success rates (which retrieval sources are pulling weight)
 */

const SIGNAL_META = {
    source_grounded: { Icon: ShieldCheck, label: "Source grounded", color: "text-emerald-700 bg-emerald-100 border-emerald-200" },
    general_info: { Icon: ShieldAlert, label: "General info", color: "text-amber-800 bg-amber-100 border-amber-200" },
    unable_to_verify: { Icon: ShieldQuestion, label: "Unable to verify", color: "text-orange-800 bg-orange-100 border-orange-200" },
    legacy: { Icon: FileText, label: "Legacy (pre-grounding)", color: "text-muted-foreground bg-muted border-border" },
};

const PROVIDER_META = {
    CourtListener: { Icon: Scale, color: "text-amber-700" },
    OpenStates: { Icon: Landmark, color: "text-emerald-700" },
    GovInfo: { Icon: FileText, color: "text-sky-700" },
};

export default function AdminResearchQualityTab() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(`/admin/research-quality?days=${days}`);
            setData(data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't load research-quality data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

    if (!data && loading) {
        return <div className="flex items-center gap-2 text-sm text-muted-foreground py-8" data-testid="research-quality-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading research quality…
        </div>;
    }
    if (error && !data) {
        return <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="research-quality-error">
            {error} <button onClick={load} className="underline ml-2">Retry</button>
        </div>;
    }
    if (!data) return null;

    const total = Object.values(data.by_confidence_signal || {}).reduce((s, n) => s + n, 0);

    return (
        <div className="space-y-6" data-testid="admin-research-quality">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-serif text-2xl font-bold">Legal AI research quality</h2>
                <div className="flex items-center gap-2">
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="ll-input py-1.5 text-sm"
                        data-testid="research-quality-window-select"
                    >
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                        <option value={365}>Last 365 days</option>
                    </select>
                    <button onClick={load} disabled={loading}
                        className="inline-flex items-center gap-1 text-sm rounded-full border border-border px-3 py-1.5 hover:bg-muted"
                        data-testid="research-quality-refresh">
                        <RefreshCcw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </div>

            {/* Confidence histogram */}
            <div className="ll-card">
                <h3 className="font-serif text-lg font-semibold mb-3">Confidence breakdown</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="research-confidence-grid">
                    {["source_grounded", "general_info", "unable_to_verify", "legacy"].map((sig) => {
                        const m = SIGNAL_META[sig];
                        const Icon = m.Icon;
                        const n = data.by_confidence_signal?.[sig] || 0;
                        const pct = total ? Math.round((n / total) * 100) : 0;
                        return (
                            <div key={sig} className={`rounded-lg border px-3 py-2.5 ${m.color}`} data-testid={`research-signal-${sig}`}>
                                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold mb-1">
                                    <Icon className="h-3 w-3" /> {m.label}
                                </div>
                                <div className="font-serif text-2xl font-bold tabular-nums">{n}</div>
                                <div className="text-[11px] opacity-80">{pct}% of last {days} days</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Provider success rates */}
            <div className="ll-card">
                <h3 className="font-serif text-lg font-semibold mb-2">Source providers</h3>
                <p className="text-xs text-muted-foreground mb-3">
                    Out of <strong>{data.total_with_providers}</strong> retrieval-grounded queries in the last {days} days, how often each provider returned at least one hit.
                </p>
                <div className="space-y-2" data-testid="research-providers">
                    {/* Pinned display order so percentages don't jitter across refreshes
                    due to dict iteration order on the backend. */}
                    {["CourtListener", "OpenStates", "GovInfo"].map((name) => {
                        const count = data.provider_counts?.[name] ?? 0;
                        const m = PROVIDER_META[name] || { Icon: FileText, color: "text-foreground" };
                        const Icon = m.Icon;
                        const pct = data.total_with_providers ? Math.round((count / data.total_with_providers) * 100) : 0;
                        return (
                            <div key={name} className="flex items-center gap-3" data-testid={`research-provider-${name}`}>
                                <Icon className={`h-4 w-4 ${m.color} flex-shrink-0`} />
                                <div className="flex-1 text-sm">
                                    <div className="flex justify-between font-semibold">
                                        <span>{name}</span>
                                        <span className="tabular-nums">{count} · {pct}%</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden">
                                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Weak topics by category + state */}
            <div className="grid md:grid-cols-2 gap-4">
                <div className="ll-card">
                    <h3 className="font-serif text-lg font-semibold mb-2">Weakest categories</h3>
                    <p className="text-xs text-muted-foreground mb-3">Topics where indexed sources couldn't verify the question. Candidates for canned content or expanded retrieval.</p>
                    <ul className="space-y-1.5 text-sm" data-testid="research-weak-by-category">
                        {(data.weak_by_category || []).map((c, i) => (
                            <li key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                                <span>{c.category_name || c.category_slug || "(uncategorized)"}</span>
                                <span className="tabular-nums font-semibold text-orange-700">{c.count}</span>
                            </li>
                        ))}
                        {(data.weak_by_category || []).length === 0 && (
                            <li className="text-xs text-muted-foreground">No unverified questions in this window.</li>
                        )}
                    </ul>
                </div>
                <div className="ll-card">
                    <h3 className="font-serif text-lg font-semibold mb-2">Weakest states</h3>
                    <p className="text-xs text-muted-foreground mb-3">States where retrieval coverage is thinnest — useful for prioritizing state-specific source ingestion.</p>
                    <ul className="space-y-1.5 text-sm" data-testid="research-weak-by-state">
                        {(data.weak_by_state || []).map((s, i) => (
                            <li key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                                <span>{s.state_name} <span className="text-muted-foreground">({s.state_code})</span></span>
                                <span className="tabular-nums font-semibold text-orange-700">{s.count}</span>
                            </li>
                        ))}
                        {(data.weak_by_state || []).length === 0 && (
                            <li className="text-xs text-muted-foreground">No unverified questions in this window.</li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Recent weak questions */}
            <div className="ll-card">
                <h3 className="font-serif text-lg font-semibold mb-2">Recent weak-answer questions</h3>
                <p className="text-xs text-muted-foreground mb-3">Newest user questions the indexed sources couldn't verify. Review for canned-response or content opportunities.</p>
                <ul className="space-y-2" data-testid="research-recent-weak">
                    {(data.recent_weak || []).slice(0, 25).map((q, i) => (
                        <li key={i} className="rounded-md border border-border p-3" data-testid={`research-weak-item-${i}`}>
                            <div className="text-sm text-foreground leading-relaxed">{q.question}</div>
                            <div className="text-[11px] text-muted-foreground mt-1.5 flex flex-wrap gap-2">
                                <span>{q.category_name || "—"}</span>
                                <span>·</span>
                                <span>{q.state_code || "—"}</span>
                                {q.cross_state_query && <><span>·</span><span className="text-emerald-700 font-medium">cross-state query</span></>}
                                {q.providers_succeeded?.length > 0 ? (
                                    <><span>·</span><span>sources hit: {q.providers_succeeded.join(", ")}</span></>
                                ) : (
                                    <><span>·</span><span className="text-orange-700">no provider hits</span></>
                                )}
                            </div>
                        </li>
                    ))}
                    {(data.recent_weak || []).length === 0 && (
                        <li className="text-xs text-muted-foreground">No unverified questions in this window.</li>
                    )}
                </ul>
            </div>
        </div>
    );
}
